import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { CalculatorInput } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateCashOnCash,
  calculateCapRate,
  calculateDSCR,
  calculateGRM,
  formatCurrency,
  formatPercent,
} from '@/lib/calculators/formulas'

interface Property {
  name: string
  purchasePrice: number
  monthlyRent: number
  monthlyExpenses: number
  downPaymentPercent: number
  interestRate: number
}

const defaultProperty = (): Property => ({
  name: '',
  purchasePrice: 200000,
  monthlyRent: 2000,
  monthlyExpenses: 400,
  downPaymentPercent: 25,
  interestRate: 7,
})

export default function PropertyComparisonSheetPage() {
  const [properties, setProperties] = useState<Property[]>([
    { ...defaultProperty(), name: 'Property A', purchasePrice: 170000, monthlyRent: 2400, monthlyExpenses: 380 },
    { ...defaultProperty(), name: 'Property B', purchasePrice: 220000, monthlyRent: 2800, monthlyExpenses: 450 },
  ])

  const updateProperty = (index: number, updates: Partial<Property>) => {
    setProperties((prev) => prev.map((p, i) => (i === index ? { ...p, ...updates } : p)))
  }

  const addProperty = () => {
    if (properties.length < 4) {
      const letter = String.fromCharCode(65 + properties.length)
      setProperties((prev) => [...prev, { ...defaultProperty(), name: `Property ${letter}` }])
    }
  }

  const removeProperty = (index: number) => {
    if (properties.length > 2) {
      setProperties((prev) => prev.filter((_, i) => i !== index))
    }
  }

  const analyze = (p: Property) => {
    const vacancy = 0.08
    const downPayment = p.purchasePrice * (p.downPaymentPercent / 100)
    const loanAmount = p.purchasePrice - downPayment
    const monthlyDebt = calculateMonthlyPayment(loanAmount, p.interestRate, 30)
    const effectiveRent = p.monthlyRent * (1 - vacancy)
    const monthlyNOI = effectiveRent - p.monthlyExpenses
    const annualNOI = monthlyNOI * 12
    const monthlyCashFlow = monthlyNOI - monthlyDebt
    const annualCashFlow = monthlyCashFlow * 12
    const totalCashIn = downPayment + 5000 // estimated closing costs

    return {
      capRate: calculateCapRate(annualNOI, p.purchasePrice),
      cashOnCash: calculateCashOnCash(annualCashFlow, totalCashIn),
      dscr: calculateDSCR(monthlyNOI, monthlyDebt),
      grm: calculateGRM(p.purchasePrice, p.monthlyRent * 12),
      monthlyCashFlow,
      annualCashFlow,
      totalCashIn,
      monthlyDebt,
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8 p-4 py-12">
      <Link to="/resources" className="text-amber-600 hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-3xl font-bold">Property Comparison Sheet</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            Compare properties side by side on the metrics that matter. Enter the basics for each property and see cap
            rate, cash-on-cash, DSCR, and cash flow calculated automatically. Uses 8% vacancy and $5K closing costs as
            defaults.
          </p>
        </CardContent>
      </Card>

      {/* Input Cards */}
      <div
        className={`grid gap-6 ${properties.length <= 2 ? 'md:grid-cols-2' : properties.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-4'}`}
      >
        {properties.map((property, index) => (
          <Card key={index}>
            <CardContent className="space-y-3 p-5">
              <div className="flex items-center justify-between">
                <input
                  type="text"
                  value={property.name}
                  onChange={(e) => updateProperty(index, { name: e.target.value })}
                  className="w-full border-none bg-transparent text-lg font-semibold outline-none"
                  placeholder="Property name"
                />
                {properties.length > 2 && (
                  <button
                    onClick={() => removeProperty(index)}
                    className="text-muted-foreground ml-2 text-sm hover:text-red-400"
                  >
                    Remove
                  </button>
                )}
              </div>

              <CalculatorInput
                label="Purchase Price"
                value={property.purchasePrice}
                onChange={(v) => updateProperty(index, { purchasePrice: v })}
                prefix="$"
              />
              <CalculatorInput
                label="Monthly Rent"
                value={property.monthlyRent}
                onChange={(v) => updateProperty(index, { monthlyRent: v })}
                prefix="$"
              />
              <CalculatorInput
                label="Monthly Expenses"
                value={property.monthlyExpenses}
                onChange={(v) => updateProperty(index, { monthlyExpenses: v })}
                prefix="$"
              />
              <CalculatorInput
                label="Down Payment"
                value={property.downPaymentPercent}
                onChange={(v) => updateProperty(index, { downPaymentPercent: v })}
                suffix="%"
              />
              <CalculatorInput
                label="Interest Rate"
                value={property.interestRate}
                onChange={(v) => updateProperty(index, { interestRate: v })}
                suffix="%"
              />
            </CardContent>
          </Card>
        ))}
      </div>

      {properties.length < 4 && (
        <div className="text-center">
          <Button variant="outline" onClick={addProperty}>
            + Add Property
          </Button>
        </div>
      )}

      <Separator />

      {/* Comparison Table */}
      <h2 className="text-2xl font-semibold">Side-by-Side Analysis</h2>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-muted border-b">
              <th className="p-3 text-left font-semibold">Metric</th>
              {properties.map((p, i) => (
                <th key={i} className="p-3 text-left font-semibold">
                  {p.name || `Property ${i + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-muted-foreground">
            {(() => {
              const analyses = properties.map(analyze)
              const bestCapRate = Math.max(...analyses.map((a) => a.capRate))
              const bestCoC = Math.max(...analyses.map((a) => a.cashOnCash))
              const bestDSCR = Math.max(...analyses.map((a) => a.dscr))
              const bestCashFlow = Math.max(...analyses.map((a) => a.monthlyCashFlow))

              const rows = [
                {
                  label: 'Cap Rate',
                  values: analyses.map((a) => formatPercent(a.capRate)),
                  highlights: analyses.map((a) => a.capRate === bestCapRate),
                },
                {
                  label: 'Cash-on-Cash',
                  values: analyses.map((a) => formatPercent(a.cashOnCash)),
                  highlights: analyses.map((a) => a.cashOnCash === bestCoC),
                },
                {
                  label: 'DSCR',
                  values: analyses.map((a) => `${a.dscr.toFixed(2)}x`),
                  highlights: analyses.map((a) => a.dscr === bestDSCR),
                },
                {
                  label: 'GRM',
                  values: analyses.map((a) => a.grm.toFixed(1) + 'x'),
                  highlights: analyses.map(() => false),
                },
                {
                  label: 'Monthly Cash Flow',
                  values: analyses.map((a) => formatCurrency(a.monthlyCashFlow)),
                  highlights: analyses.map((a) => a.monthlyCashFlow === bestCashFlow),
                },
                {
                  label: 'Annual Cash Flow',
                  values: analyses.map((a) => formatCurrency(a.annualCashFlow)),
                  highlights: analyses.map(() => false),
                },
                {
                  label: 'Monthly Debt Service',
                  values: analyses.map((a) => formatCurrency(a.monthlyDebt)),
                  highlights: analyses.map(() => false),
                },
                {
                  label: 'Total Cash In',
                  values: analyses.map((a) => formatCurrency(a.totalCashIn)),
                  highlights: analyses.map(() => false),
                },
              ]

              return rows.map((row) => (
                <tr key={row.label} className="border-muted/50 border-b">
                  <td className="p-3 font-medium">{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i} className={`p-3 ${row.highlights[i] ? 'font-semibold text-amber-500' : ''}`}>
                      {val}
                    </td>
                  ))}
                </tr>
              ))
            })()}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground text-xs">
        Best value in each row is highlighted. Uses 8% vacancy, 30-year loan term, and $5,000 estimated closing costs.
      </p>
    </div>
  )
}
